import React, { useState } from 'react';
import { Key, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';

const ApiKeyPrompt = ({ onApiKeySubmit }) => {
  const [apiKey, setApiKey] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!apiKey.trim()) {
      setError('Please enter your RocketLane API key');
      return;
    }

    setIsLoading(true);
    setError('');
    setIsValidating(true);

    try {
      await onApiKeySubmit(apiKey.trim());
    } catch (err) {
      setError(err.message || 'Failed to validate API key. Please check your key and try again.');
    } finally {
      setIsLoading(false);
      setIsValidating(false);
    }
  };

  const validateApiKeyFormat = (key) => {
    // RocketLane API keys typically start with 'rl-' and are followed by a UUID
    const apiKeyPattern = /^rl-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/;
    return apiKeyPattern.test(key);
  };

  const isKeyFormatValid = apiKey && validateApiKeyFormat(apiKey);

  return (
    <div className="api-key-prompt-overlay">
      <div className="api-key-prompt-modal">
        <div className="api-key-prompt-header">
          <div className="api-key-prompt-logo">
            <Key size={32} />
            <h1>RocketLane Dashboard</h1>
          </div>
          <p className="api-key-prompt-subtitle">
            Enter your RocketLane API key to access your dashboard
          </p>
        </div>

        <form onSubmit={handleSubmit} className="api-key-prompt-form">
          <div className="form-group">
            <label htmlFor="apiKey" className="form-label">
              RocketLane API Key
            </label>
            <div className="api-key-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="apiKey"
                value={apiKey}
                onChange={(e) => {
                  setApiKey(e.target.value);
                  setError('');
                }}
                placeholder="rl-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className={`api-key-input ${error ? 'error' : ''} ${isKeyFormatValid ? 'valid' : ''}`}
                disabled={isLoading}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="toggle-password-btn"
                disabled={isLoading}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            
            {isKeyFormatValid && (
              <div className="validation-message valid">
                <CheckCircle size={14} />
                <span>Valid API key format</span>
              </div>
            )}
            
            {error && (
              <div className="validation-message error">
                <AlertCircle size={14} />
                <span>{error}</span>
              </div>
            )}
          </div>

          <div className="api-key-help">
            <h4>How to get your API key:</h4>
            <ol>
              <li>Log in to your RocketLane account</li>
              <li>Go to Settings → API Keys</li>
              <li>Click "Generate New API Key"</li>
              <li>Copy the generated key (starts with "rl-")</li>
              <li>Paste it above and click "Connect"</li>
            </ol>
            <p className="api-key-note">
              <strong>Note:</strong> Your API key is stored locally in your browser and is never sent to our servers.
            </p>
          </div>

          <button
            type="submit"
            className="connect-btn"
            disabled={isLoading || !apiKey.trim()}
          >
            {isLoading ? (
              <>
                <div className="spinner"></div>
                {isValidating ? 'Validating...' : 'Connecting...'}
              </>
            ) : (
              'Connect to RocketLane'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ApiKeyPrompt;
